import type { Client } from '../../api/client.js';
import { creerSourceDossiers } from '../dossier/source-liste.js';
import { listerTousLesDossiers, lireParDossier } from '../dossier/agregat-par-dossier.js';
import type { DocumentLakou } from './types.js';

/**
 * Liste transversale des documents (D09, équipe) : le contrat n'a pas de route « tous dossiers » ; elle agrège donc par dossier
 * (`dossiers.lister`, puis `documents.lister` et `documents.attendus` de chacun). Un dossier illisible est dit tel quel.
 */
export interface LigneDocuments {
  dossierId: string;
  titre: string;
  reference: string;
  etat: 'ok' | 'indisponible';
  pieces: number;
  aVerifier: number;
  refusees: number;
  /** Pièces attendues pas encore reçues (« attendu » ou « demandé »). */
  attendues: number;
}

export interface SourceListeDocuments {
  charger: () => Promise<LigneDocuments[]>;
}

export const aTraiterDocuments = (l: LigneDocuments): boolean => l.etat === 'ok' && l.aVerifier + l.refusees + l.attendues > 0;

export function creerSourceListeDocuments(client: Client): SourceListeDocuments {
  return {
    async charger() {
      const dossiers = await listerTousLesDossiers(creerSourceDossiers(client));
      const lectures = await lireParDossier(dossiers, async (d) => {
        const parametres = { dossierId: d.id };
        const [documents, attendus] = await Promise.all([
          client.appeler<Pick<DocumentLakou, 'status'>[]>('documents.lister', { parametres }),
          client.appeler<{ status: string }[]>('documents.attendus', { parametres }),
        ]);
        return { documents, attendus };
      });
      return lectures.map((r): LigneDocuments => {
        const base = { dossierId: r.dossier.id, titre: r.dossier.title, reference: r.dossier.reference };
        if (!r.ok) return { ...base, etat: 'indisponible', pieces: 0, aVerifier: 0, refusees: 0, attendues: 0 };
        const { documents, attendus } = r.valeur;
        return {
          ...base,
          etat: 'ok',
          pieces: documents.length,
          aVerifier: documents.filter((d) => d.status === 'a_verifier').length,
          refusees: documents.filter((d) => d.status === 'refuse').length,
          attendues: attendus.filter((a) => a.status === 'attendu' || a.status === 'demande').length,
        };
      });
    },
  };
}
